import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Users, CheckCircle, XCircle, Clock, RefreshCw, ChevronRight, User, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const PatientViewNotifications = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [processing, setProcessing] = useState(false);

  // Check if user is an approver (Lead Therapist, Clinical Lead, Director, Super Admin)
  const jobTitle = (user?.job_title || '').toLowerCase();
  const isLeadTherapist = ['lead', 'senior', 'head', 'chief', 'principal'].some(term => jobTitle.includes(term));
  const approverRoles = ['director', 'managing_director', 'clinical_lead'];
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  const isApprover = isSuperAdmin || approverRoles.includes(user?.role) || isLeadTherapist;

  const fetchNotifications = useCallback(async () => {
    if (!isApprover) {
      setLoading(false);
      return;
    }
    
    try {
      // Fetch general notifications and filter for patient view requests
      const response = await api.get('/auth/notifications');
      const viewNotifs = (response.data || []).filter(
        n => n.type === 'patient_view_request'
      );
      setNotifications(viewNotifs);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [api, isApprover]);

  useEffect(() => {
    fetchNotifications();
    // Refresh every 2 minutes
    const interval = setInterval(fetchNotifications, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/auth/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleApprove = async () => {
    if (!selectedNotification?.request_id) return;
    
    setProcessing(true);
    try {
      await api.put(`/patient-view-approvals/${selectedNotification.request_id}/approve`);
      
      // Mark notification as read
      await markAsRead(selectedNotification.id);
      
      toast.success('Patient view access approved');
      setShowApprovalDialog(false);
      setSelectedNotification(null);
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeny = async () => {
    if (!selectedNotification?.request_id) return;
    
    setProcessing(true);
    try {
      await api.put(`/patient-view-approvals/${selectedNotification.request_id}/revoke`);
      
      // Mark notification as read
      await markAsRead(selectedNotification.id);
      
      toast.success('Patient view access denied');
      setShowApprovalDialog(false);
      setSelectedNotification(null);
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deny request');
    } finally {
      setProcessing(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  // Don&apos;t render if user is not an approver
  if (!isApprover) {
    return null;
  }

  const unreadCount = notifications.length;

  // Don&apos;t show badge if no notifications
  if (unreadCount === 0 && !open) {
    return null;
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            data-testid="patient-view-notifications-btn"
          >
            <Users className="h-5 w-5 text-blue-600" />
            {unreadCount > 0 && (
              <Badge 
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-blue-500 text-white text-xs animate-pulse"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96 p-0" align="end">
          <div className="p-3 border-b bg-blue-50">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Patient View Requests
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => fetchNotifications()}
                className="h-8 w-8 p-0"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            {unreadCount > 0 && (
              <p className="text-xs text-blue-600 mt-1">
                {unreadCount} request{unreadCount > 1 ? 's' : ''} for cross-therapist patient access
              </p>
            )}
          </div>
          
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-slate-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                <p className="font-medium">No pending requests</p>
                <p className="text-sm">All patient view requests have been processed</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notif) => (
                  <div 
                    key={notif.id}
                    className="p-3 hover:bg-blue-50/50 cursor-pointer transition-colors"
                    onClick={() => {
                      setSelectedNotification(notif);
                      setShowApprovalDialog(true);
                    }}
                    data-testid={`view-notif-${notif.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 line-clamp-2">
                          {notif.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-slate-400">
                            {formatDate(notif.created_at)} at {formatTime(notif.created_at)}
                          </span>
                          <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="p-2 border-t bg-slate-50">
            <Button 
              variant="ghost" 
              className="w-full justify-between text-sm"
              onClick={() => {
                setOpen(false);
                navigate('/patients');
              }}
            >
              View all in Patients page
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-800">
              <Eye className="w-5 h-5" />
              Patient View Access Request
            </DialogTitle>
            <DialogDescription>
              Review and approve or deny this cross-therapist patient view request
            </DialogDescription>
          </DialogHeader>
          
          {selectedNotification && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-700 mb-3">
                  {selectedNotification.message}
                </p>
                <p className="text-xs text-slate-500">
                  Requested: {formatDate(selectedNotification.created_at)} at {formatTime(selectedNotification.created_at)}
                </p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-blue-700">
                  <strong>Note:</strong> This approval grants permanent view access until manually revoked. 
                  The staff member will be able to see this patient&apos;s records, assessments, and evaluations.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="outline" 
              className="border-red-200 text-red-600 hover:bg-red-50"
              onClick={handleDeny}
              disabled={processing}
              data-testid="deny-view-dialog-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Deny
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={handleApprove}
              disabled={processing}
              data-testid="approve-view-dialog-btn"
            >
              {processing ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve Access
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PatientViewNotifications;
